/**
 * Generic TanStack Query building blocks shared by every settings domain.
 *
 * These keep the per-domain hooks (see `./index.ts`) tiny and consistent:
 *  - {@link useSettingsQuery} — read a settings resource, typed errors as
 *    {@link NormalizedApiError} (the shared client already normalises failures).
 *  - {@link useSettingsMutation} — update a settings resource and write the
 *    server response straight back into the cache (so `version`/audit refresh).
 *  - {@link useInvalidatingMutation} — for write actions whose response is *not*
 *    the cached entity (provider create/update/delete), invalidate a list key.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey, UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

/** Read a settings resource as tenant-scoped server state. */
export function useSettingsQuery<TData>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  options?: { enabled?: boolean },
): UseQueryResult<TData, NormalizedApiError> {
  return useQuery<TData, NormalizedApiError>({
    queryKey,
    queryFn,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Update a settings resource. On success the returned resource (with a fresh
 * `version`/`updatedAt`) is written into the cache under `queryKey`, so the
 * editing screen immediately reflects the new optimistic-lock version.
 */
export function useSettingsMutation<TData, TVariables>(
  queryKey: QueryKey,
  mutationFn: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, NormalizedApiError, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<TData, NormalizedApiError, TVariables>({
    mutationFn,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });
}

/**
 * Run a write action and invalidate `invalidateKey` on success. Used where the
 * response is not the cached entity (e.g. provider CRUD invalidates the list).
 */
export function useInvalidatingMutation<TData, TVariables>(
  invalidateKey: QueryKey,
  mutationFn: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, NormalizedApiError, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<TData, NormalizedApiError, TVariables>({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invalidateKey });
    },
  });
}
